import { LaborRateGridLibrary, LaborRateOverrides } from '../../api/dealer-api-interface-all';
import { tryParseFloat } from '../../../webmodule-common/other/number-utilities';

// The two independently overridable halves of a cell, named after the grids that hold them.
export type OverrideHalf = 'rates' | 'factors';

// Indexed [layer][area][type]; null where the fabricator has left the V6 default in place.
export type OverrideGrid = (string | null)[][][];

export interface CellPosition {
  layer: number;
  area: number;
  type: number;
}

export interface EditableLaborLibrary {
  // V6's value for every cell. Its axes index both override grids.
  defaults: LaborRateGridLibrary;
  rates: OverrideGrid;
  factors: OverrideGrid;
}

const halves: OverrideHalf[] = ['rates', 'factors'];

// Why scrub on every keystroke rather than validate after the fact? A character the field would only
// discard later cannot be typed at all, which removes the need for a save-blocking check entirely.
// Keeps at most one decimal point: the house input-money's own handleInput comment claims "a single
// decimal point" but its regex (/[^0-9.e-]/g) never enforces that, so it still lets a sign, an "e" or
// a second dot through.
export function scrubOverrideInput(value: string): string {
  const digitsAndDots = value.replace(/[^0-9.]/g, '');
  const firstDot = digitsAndDots.indexOf('.');
  if (firstDot === -1) return digitsAndDots;
  return digitsAndDots.slice(0, firstDot + 1) + digitsAndDots.slice(firstDot + 1).replace(/\./g, '');
}

export interface ScrubbedEntry {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

// Scrubs a field's text and says where its selection lands afterwards. Why work the caret out at all?
// Writing .value back snaps the caret to the end, so correcting a digit mid-value would throw the
// cursor away on every keystroke. Why scrub the text before each caret rather than subtract the total
// removed? Only what was removed ahead of the caret moves it: typing "." at the start of "1.5" strips
// the later dot, and the caret belongs after the dot just typed. A kept character never depends on
// anything after it, so scrubbing a prefix gives exactly the prefix of the scrubbed whole.
export function scrubKeepingSelection(value: string, selectionStart: number, selectionEnd: number): ScrubbedEntry {
  return {
    value: scrubOverrideInput(value),
    selectionStart: scrubOverrideInput(value.slice(0, selectionStart)).length,
    selectionEnd: scrubOverrideInput(value.slice(0, selectionEnd)).length
  };
}

// Applied when a cell commits rather than on every keystroke, so a value can sit half-typed (a bare
// ".") while the field still has focus. Fills in the punctuation scrubOverrideInput leaves implicit
// and, for rates only, forces two decimals the way the house input-money's handleChange does for money.
export function normalizeOverrideValue(value: string, half: OverrideHalf): string | null {
  const scrubbed = scrubOverrideInput(value);
  if (scrubbed === '' || scrubbed === '.') return null;
  // Why return rates before the two repairs below? parseFloat already absorbs both a leading and a
  // trailing decimal point, so those repairs only ever apply to the factors half. And why not force
  // two decimals on factors too? A factor is a multiplier, so rounding 1.125 to 1.13 would corrupt
  // it; only rates are money.
  if (half === 'rates') return parseFloat(scrubbed).toFixed(2);
  const withLeadingZero = scrubbed.startsWith('.') ? `0${scrubbed}` : scrubbed;
  return withLeadingZero.endsWith('.') ? withLeadingZero.slice(0, -1) : withLeadingZero;
}

export function buildEditableLibraries(
  matrix: LaborRateGridLibrary[],
  overrides: LaborRateOverrides
): EditableLaborLibrary[] {
  const saved = new Map(overrides.libraries.map(library => [library.libraryCode, library]));
  return matrix.map(defaults => {
    const savedLibrary = saved.get(defaults.libraryCode);
    return {
      defaults,
      rates: overlayGrid(defaults, savedLibrary?.rates, 'rates'),
      factors: overlayGrid(defaults, savedLibrary?.factors, 'factors')
    };
  });
}

export function setOverride(
  library: EditableLaborLibrary,
  half: OverrideHalf,
  cell: CellPosition,
  input: string
): void {
  library[half][cell.layer][cell.area][cell.type] = normalizeOverrideValue(input, half);
}

export function isOverridden(library: EditableLaborLibrary, half: OverrideHalf, cell: CellPosition): boolean {
  return library[half][cell.layer][cell.area][cell.type] !== null;
}

// Why guard every index when the axes say the cell exists? NSwag types the grids as string[][][], but
// that is the generator dropping C#'s nullability rather than a promise from V6, so a library can
// arrive with a short or empty grid. An empty cell is a better outcome than a TypeError mid-render.
export function defaultValue(library: EditableLaborLibrary, half: OverrideHalf, cell: CellPosition): string {
  return library.defaults[half][cell.layer]?.[cell.area]?.[cell.type] ?? '';
}

// What the cell shows: the override when there is one, otherwise V6's default as real, editable text.
// Why not a placeholder for the default? A placeholder is not part of the field's content, so backspace,
// arrow keys and selection have nothing to act on, which makes a default cell look uneditable.
// Why the raw string while focused? Reformatting mid-edit (money punctuation) would fight the caret.
// Why leave a non-numeric value unformatted? The money formatter renders it as NaN.
export function cellDisplayValue(
  library: EditableLaborLibrary,
  half: OverrideHalf,
  cell: CellPosition,
  focused: boolean,
  formatRate: (value: string) => string
): string {
  const value = library[half][cell.layer][cell.area][cell.type] ?? defaultValue(library, half, cell);
  if (focused || half === 'factors' || tryParseFloat(value) === undefined) return value;
  return formatRate(value);
}

export function toOverridesPayload(libraries: EditableLaborLibrary[]): LaborRateOverrides {
  return {
    // Why leave out libraries with nothing overridden? The set is sent to V6 on every priced quote, and
    // an all-null library would only make V6 walk a grid with nothing in it.
    libraries: libraries.filter(hasAnyOverride).map(
      // Why annotate the callback when the cast below compiles without it? The Omit is what marks
      // libraryDescription as the one deliberate absence, so a misspelled field is reported at the
      // field (TS2561, with a spelling suggestion) rather than as an untyped pair at the cast.
      (library): Omit<LaborRateGridLibrary, 'libraryDescription'> => ({
        libraryCode: library.defaults.libraryCode,
        // Why leave the description out? Nothing reads it back off the blob: the backend reconciler
        // overwrites it from the live library on save, so storing our own copy here would only go stale
        // against V6.
        areas: library.defaults.areas,
        types: library.defaults.types,
        layers: library.defaults.layers,
        // Why the casts? NSwag drops the nullability inside C#'s nested string? lists, so the generated
        // type claims string[][][] although the API takes null for a cell left at its default.
        rates: library.rates as string[][][],
        factors: library.factors as string[][][]
      })
    ) as LaborRateGridLibrary[]
  };
}

// Compared against the snapshot taken at load or save to tell whether the tab has unsaved changes.
export function overridesSnapshot(libraries: EditableLaborLibrary[]): string {
  return JSON.stringify(toOverridesPayload(libraries));
}

function hasAnyOverride(library: EditableLaborLibrary): boolean {
  return halves.some(half => library[half].some(areas => areas.some(types => types.some(value => value !== null))));
}

// Why shape the grid from V6's axes rather than copy the saved one? The saved grid is already indexed
// against these axes, but building from them guarantees every cell the view indexes exists even when a
// saved grid arrives short, and it gives edits their own arrays, so a revert (which rebuilds from the
// loaded result) is not undone by an edit that aliased it.
// Why normalize stored values too, not just freshly typed ones? A malformed value can only have
// arrived from stored data - the UI cannot produce one anymore - and with validation gone the next
// save would otherwise write it straight back out unchanged. And why doesn't the repair mark the tab
// dirty? The unsaved-changes snapshot is taken after this runs, so merely opening the tab does not
// trigger a save on leave; the repair goes out with the next real edit instead.
function overlayGrid(
  defaults: LaborRateGridLibrary,
  saved: OverrideGrid | undefined,
  half: OverrideHalf
): OverrideGrid {
  return defaults.layers.map((_layerCode, layer) =>
    defaults.areas.map((_areaCode, area) =>
      defaults.types.map((_typeCode, type) => {
        const value = saved?.[layer]?.[area]?.[type] ?? null;
        return value === null ? null : normalizeOverrideValue(value, half);
      })
    )
  );
}
